import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, IconButton,
} from "@mui/material";
import { Close, AccessTimeFilled } from "@mui/icons-material";
import { crmService, OverdueLead } from "../../services/crmService";
import { colors, typography } from "../../theme/designSystem";

// Aviso de lead parado além do prazo do estágio (RN-622). Aparece UMA VEZ por sessão, de
// propósito: um modal que reaparece a cada navegação vira ruído e as pessoas passam a fechá-lo
// no automático — aí ele deixa de avisar qualquer coisa. Depois desta vez, o mesmo dado vive
// no contador "parados" do card e no widget do Dashboard.
const SESSION_KEY = "clinic4us-crm-overdue-seen";

interface OverdueLeadsModalProps {
  entityId: string;
  onOpenLead: (leadId: string, pipelineId: string) => void;
}

const OverdueLeadsModal: React.FC<OverdueLeadsModalProps> = ({ entityId, onOpenLead }) => {
  const [leads, setLeads] = useState<OverdueLead[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!entityId) return;

    let alreadySeen = false;
    try {
      alreadySeen = sessionStorage.getItem(SESSION_KEY) === "1";
    } catch {
      // navegador sem sessionStorage: mostra uma vez por carregamento, o que é aceitável
    }
    if (alreadySeen) return;

    crmService
      .getOverdueLeads(entityId)
      .then((rows) => {
        if (rows.length === 0) return;
        setLeads(rows);
        setOpen(true);
        try {
          sessionStorage.setItem(SESSION_KEY, "1");
        } catch {
          // sem sessionStorage o aviso reaparece no próximo carregamento; não é motivo de erro
        }
      })
      .catch(() => {
        // sem permissão de CRM ou falha: o aviso simplesmente não aparece
      });
  }, [entityId]);

  if (!open || leads.length === 0) return null;

  return (
    <Dialog open onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          backgroundColor: colors.primary,
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AccessTimeFilled />
          <Typography sx={{ fontWeight: typography.fontWeight.semibold }}>
            {leads.length === 1
              ? "1 lead passou do prazo"
              : `${leads.length} leads passaram do prazo`}
          </Typography>
        </Box>
        <IconButton onClick={() => setOpen(false)} sx={{ color: "white" }} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5 }}>
        <Typography sx={{ fontSize: typography.fontSize.sm, color: colors.textSecondary, mb: 2 }}>
          Tempo contado no horário de funcionamento da clínica — fim de semana e feriado não contam.
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {leads.map((lead) => (
            <Box
              key={lead.leadId}
              onClick={() => onOpenLead(lead.leadId, lead.pipelineId)}
              sx={{
                border: "1px solid #f5c6cb",
                backgroundColor: "#fdf3f4",
                borderRadius: "8px",
                p: 1.25,
                cursor: "pointer",
                "&:hover": { backgroundColor: "#fbe9eb" },
              }}
            >
              <Typography sx={{ fontWeight: typography.fontWeight.semibold, color: colors.textPrimary }}>
                {lead.leadName}
              </Typography>
              <Typography sx={{ fontSize: typography.fontSize.sm, color: "#842029" }}>
                {lead.stageName} · {lead.elapsedBusinessHours}h úteis paradas (limite {lead.maxBusinessHours}h)
              </Typography>
              <Typography sx={{ fontSize: typography.fontSize.xs, color: colors.textSecondary }}>
                {lead.pipelineName}
                {lead.assignedToName ? ` · ${lead.assignedToName}` : " · sem responsável"}
              </Typography>
            </Box>
          ))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => setOpen(false)} sx={{ textTransform: "none", color: colors.textSecondary }}>
          Ver depois
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OverdueLeadsModal;
